import React, { useState, useEffect } from 'react';
import axios from '../../../Data/Axios';
import './RegistrationModal.scss';

interface RegistrationModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const RegistrationModal: React.FC<RegistrationModalProps> = ({ isOpen, onClose }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [countryCode, setCountryCode] = useState('+62');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        country: '',
        studyGoal: '',
        questions: ''
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name || !formData.email || !formData.phone) {
            alert('Please fill in all required fields');
            return;
        }

        setIsSubmitting(true);

        try {
            const currentDate = new Date().toLocaleString();
            const htmlContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <title>GKS Seminar Registration</title>
                    <style>
                        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 20px; background-color: #f4f4f4; }
                        .container { max-width: 600px; margin: 0 auto; background: white; border-radius: 10px; overflow: hidden; box-shadow: 0 0 20px rgba(0,0,0,0.1); }
                        .header { background: linear-gradient(135deg, #e94e97 0%, #f27ca7 100%); color: white; padding: 30px; text-align: center; }
                        .content { padding: 30px; }
                        .info-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                        .info-table th, .info-table td { border: 1px solid #ddd; padding: 12px; text-align: left; }
                        .info-table th { background-color: #f8f9fa; font-weight: bold; }
                        .footer { background-color: #f8f9fa; padding: 20px; text-align: center; color: #666; font-size: 14px; }
                    </style>
                </head>
                <body>
                    <div class="container">
                        <div class="header">
                            <h1>🎓 GKS Seminar Registration</h1>
                            <p>New participant registration</p>
                        </div>
                        <div class="content">
                            <h2>Registration Details</h2>
                            <table class="info-table">
                                <tr>
                                    <th>Field</th>
                                    <th>Response</th>
                                </tr>
                                <tr>
                                    <td><strong>Name</strong></td>
                                    <td>${formData.name}</td>
                                </tr>
                                <tr>
                                    <td><strong>Email</strong></td>
                                    <td>${formData.email}</td>
                                </tr>
                                <tr>
                                    <td><strong>Phone/WhatsApp</strong></td>
                                    <td>${countryCode}${formData.phone}</td>
                                </tr>
                                <tr>
                                    <td><strong>Country</strong></td>
                                    <td>${formData.country || 'Not specified'}</td>
                                </tr>
                                <tr>
                                    <td><strong>Study Goal</strong></td>
                                    <td>${formData.studyGoal || 'Not specified'}</td>
                                </tr>
                                <tr>
                                    <td><strong>Questions</strong></td>
                                    <td>${formData.questions || 'None'}</td>
                                </tr>
                                <tr>
                                    <td><strong>Registration Time</strong></td>
                                    <td>${currentDate}</td>
                                </tr>
                            </table>

                            <div style="background-color: #fff3cd; border: 1px solid #ffeaa7; border-radius: 8px; padding: 15px; margin: 20px 0;">
                                <p><strong>⚠️ Action Required:</strong> Please process this registration and ensure payment is completed.</p>
                            </div>
                        </div>
                        <div class="footer">
                            <p>This email was generated automatically from the GKS Seminar registration page.</p>
                            <p>Korean Mate • <a href="https://koreanmate.app">koreanmate.app</a></p>
                        </div>
                    </div>
                </body>
                </html>
            `;

            const response = await axios.post('/admin/trigger-email', {
                email: 'dl_mate@webtoon.today',
                subject: `[🎓GKS Seminar] New Registration - ${formData.name}`,
                body: htmlContent,
                groups: 'dl_mate@webtoon.today'
            });

            if (response.data && response.data.code === 200) {
                alert('Registration successful! Redirecting to payment...');
                onClose();

                setTimeout(() => {
                    window.location.href = '/payment/link?productid=GKS_seminar.250911.01';
                }, 1000);
            } else {
                throw new Error('Email sending failed');
            }
        } catch (error) {
            console.error('Form submission error:', error);
            alert('Failed to submit registration. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        const handleEscKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            window.addEventListener('keydown', handleEscKey);
            window.history.pushState({ formOpen: true }, '', window.location.href);
        }

        return () => {
            window.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        const handlePopState = (e: PopStateEvent) => {
            if (!e.state?.formOpen && isOpen) {
                onClose();
            }
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <>
            <div className="gks-modal-overlay" onClick={onClose} />
            <div className="gks-form-modal">
                <button className="close-button" onClick={onClose}>×</button>
                <h2>GKS Seminar Registration</h2>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="name">Full Name *</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter your full name"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">Email Address *</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            placeholder="your.email@example.com"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="phone">Phone/WhatsApp Number *</label>
                        <div className="phone-input-group">
                            <select
                                id="countryCode"
                                name="countryCode"
                                value={countryCode}
                                onChange={(e) => setCountryCode(e.target.value)}
                                className="country-code-select"
                            >
                                <option value="+62">+62 Indonesia</option>
                                <option value="+82">+82 South Korea</option>
                                <option value="+65">+65 Singapore</option>
                                <option value="+60">+60 Malaysia</option>
                                <option value="+66">+66 Thailand</option>
                                <option value="+84">+84 Vietnam</option>
                                <option value="+63">+63 Philippines</option>
                                <option value="+1">+1 USA/Canada</option>
                                <option value="+44">+44 UK</option>
                                <option value="+86">+86 China</option>
                                <option value="+91">+91 India</option>
                                <option value="+81">+81 Japan</option>
                                <option value="+49">+49 Germany</option>
                                <option value="+33">+33 France</option>
                                <option value="+61">+61 Australia</option>
                            </select>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                required
                                placeholder="812345678"
                                className="phone-number-input"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="country">Country of Residence</label>
                        <input
                            type="text"
                            id="country"
                            name="country"
                            value={formData.country}
                            onChange={handleInputChange}
                            placeholder="Your country"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="studyGoal">Study Goal in Korea</label>
                        <select
                            id="studyGoal"
                            name="studyGoal"
                            value={formData.studyGoal}
                            onChange={handleInputChange}
                        >
                            <option value="">Select your goal</option>
                            <option value="undergraduate">Undergraduate Degree</option>
                            <option value="masters">Master's Degree</option>
                            <option value="phd">PhD Program</option>
                            <option value="language">Korean Language Course</option>
                            <option value="exchange">Exchange Program</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="questions">Questions or Comments</label>
                        <textarea
                            id="questions"
                            name="questions"
                            value={formData.questions}
                            onChange={handleInputChange}
                            rows={4}
                            placeholder="Any specific questions about GKS?"
                        />
                    </div>

                    <button
                        type="submit"
                        className="submit-button"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Processing...' : 'Register & Proceed to Payment'}
                    </button>
                </form>
            </div>
        </>
    );
};

export default RegistrationModal;